import { useEffect, useMemo } from "react";

import { serverGetPageAssessmentStatuses } from "api/api-server-data";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectAllCourseUsersByUserId,
  selectCurrentCourseId,
  selectCurrentCourseUserId,
  selectCurrentUserId,
  selectUserAssessments,
} from "store/selectors";
import { computedStateActions } from "store/slices/computed";
import { useListSelector } from "store/store-hooks";

/**
 * Ensures all user/course data that is required to resolve assessment data is fetched.
 * This does _not_ include assessment / submission / outcome-assessment data directly.
 */
export const useFetchAssessmentBaseData = () => {
  const { canParticipateInCourse } = useCapabilities();
  const courseId = useAppSelector(selectCurrentCourseId);
  const userId = useAppSelector(selectCurrentUserId);
  const courseUserId = useAppSelector(selectCurrentCourseUserId);

  // Fetching all assessment-relevant data for the current user
  useEffect(() => {
    if (!courseUserId || !userId) {
      // This prevents an unecessary load of all course data for students while
      // the course users are still loading.
      return;
    }
    if (courseId) {
      storeApi.outcomes.list({});
      storeApi.course_outcomes.list({ course_id: courseId });
      storeApi.pages.list({
        course_id: courseId,
        // Technically we only need the unreleased pages, but we already fetch all pages in other
        // views, so dropping this filter attribute allows us to reuse the cached data.
        // released_at__isnull: false,
      });
      // Only to resolve links to pages
      storeApi.topics.list({ course_id: courseId });
      storeApi.activities.list({ course_id: courseId });

      // This will only trigger for students, for now, as staff users will need access to _everyone's_
      // data, which is too much to load on the top level of the course.
      // If a userId is passed, it means an instructor is viewing a student's assessments, so we still
      // fetch the user data.
      if (canParticipateInCourse) {
        storeApi.page_group_users.list({
          course_user_id: courseUserId,
          course_id: courseId,
        });
        storeApi.page_groups.list({
          pagegroupuser__course_user_id: courseUserId,
          course_id: courseId,
        });
      }
    }
  }, [courseUserId, courseId, userId, canParticipateInCourse]);
};

// TODO: Look into optimizing this.  We shouldn't be doing fetching for multiple courses and
// we shouldn't be doing them one course at a time.
/**
 * Fetches assessment data for multiple courses.
 * This is the core logic that both single-course and multi-course functions use.
 * If a student accesses this, the backend will limit the query to only be for their own data.
 */
const useFetchAssessmentDataForCourses = (courseIds: string[]) => {
  const userId = useAppSelector(selectCurrentUserId);

  useEffect(() => {
    if (!userId || courseIds.length === 0) {
      return;
    }

    // Fetch data for all courses
    // TODO: In the future, this could be optimized to use a single batch API call
    // to reduce network overhead and improve performance.
    const uniqueCourseIds = [...new Set(courseIds)];

    uniqueCourseIds.forEach((courseId) => {
      storeApi.course_outcomes.list({ course_id: courseId });
      storeApi.page_outcomes.list({ course_id: courseId });
      storeApi.pages.list({ course_id: courseId });
      // We don't limit this to official submissions, as we might want to show comments from
      // unofficial submission assessments as well.
      storeApi.submissions.list({ course_id: courseId });
      storeApi.assessments.list({ course_id: courseId });
      storeApi.outcome_assessments.list({ course_id: courseId });
    });
  }, [courseIds, userId]);
};

/**
 * Ensures all assessments-related data for the current course is loaded.
 */
export const useFetchAssessmentData = () => {
  const courseId = useAppSelector(selectCurrentCourseId);
  useFetchAssessmentDataForCourses(courseId ? [courseId] : []);
};

// Fetches and returns all assessments for the current user in the current course.
export const useFetchAndSelectMyAssessments = () => {
  const userId = useAppSelector(selectCurrentUserId);
  const courseId = useAppSelector(selectCurrentCourseId);
  const assessments = useAppSelector((s) => selectUserAssessments(s, userId, courseId));
  const { canParticipateInCourse } = useCapabilities();

  useFetchAssessmentBaseData();

  // Only students can "fetch my assessments", as instructors don't get assessed,
  // and this would also query way too much data for admins.
  // TODO: Org-level admins (like MP admins) who are also students in a course will accidentally
  // fetch way too much data here.;
  useFetchAssessmentDataForCourses(canParticipateInCourse && courseId ? [courseId] : []);

  return assessments;
};

export const useFetchAndSelectMyAssessmentsInOrg = () => {
  const userId = useAppSelector(selectCurrentUserId);
  const courseUsers = useListSelector((s) => selectAllCourseUsersByUserId(s, userId));
  const allAssessments = useListSelector((s) => selectUserAssessments(s, userId));

  const courseIds = useMemo(
    () =>
      courseUsers.filter((cu) => cu.role === "student").map((courseUser) => courseUser.course_id),
    [courseUsers],
  );

  useFetchAssessmentDataForCourses(courseIds);

  useEffect(() => {
    if (userId) {
      storeApi.course_users.list({ user_id: userId });
    }
  }, [userId]);

  return allAssessments;
};

export const useFetchPageAssessmentStatuses = (allPageIds: string[]) => {
  const pageIdString = allPageIds.sort().join(",");
  const userId = useAppSelector(selectCurrentUserId);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!userId) {
      return;
    }

    const loadPageAssessmentStatusData = () => {
      const pageIds = pageIdString.split(",");

      // Batch size to avoid 413 errors
      const BATCH_SIZE = 150;

      // Split pageIds into batches
      const batches: string[][] = [];
      for (let i = 0; i < pageIds.length; i += BATCH_SIZE) {
        batches.push(pageIds.slice(i, i + BATCH_SIZE));
      }

      // Process all batches in parallel (fire and forget)
      batches.forEach(async (batch) => {
        const statuses = await serverGetPageAssessmentStatuses({ pageIds: batch });

        // Dispatch statuses immediately as each batch completes
        dispatch(computedStateActions.savePageAssessmentStatuses(statuses));
      });
    };

    if (pageIdString.length > 0) {
      loadPageAssessmentStatusData();
    }
    // eslint-disable-next-line no-object-deps/no-object-deps
  }, [pageIdString, userId, dispatch]);
};
